import { useEffect, useState } from 'react';
import className from 'classnames/bind';
import { motion } from 'motion/react';
import { Container } from '@components';
import { getInducteeCategoryLabel } from '@/core/utils';

import styles from './InducteeHeroContent.module.scss';
import { AnimVals } from '@/utils/animUtils';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
const cx = className.bind(styles);

interface InducteeHeroContentProps {
  headline: { line?: string }[];
  showReverb?: boolean;
  className?: string;
  canAnimateIn?: boolean;
  hasReveal?: boolean;
  inducteeInfo: {
    inductedBy?: string;
    category: string;
    class: string;
  }
}

export default function InducteeHeroContent({ headline, inducteeInfo, showReverb, className, canAnimateIn, hasReveal }: InducteeHeroContentProps) {
  const { inductedBy, category, class: inducteeClass } = inducteeInfo;
  const { labels } = usePageWrapperData();
  const { inducted, inductedBy: inductedByLabel, category: categoryLabel } = labels || {};
  const categoryParsed = getInducteeCategoryLabel(category, labels);

  const [headlineHasLargeFont, setHeadlineHasLargeFont] = useState<boolean>(true);
  useEffect(() => {
    let longestLine = 0;
    headline?.map((line) => {
      const length = line.line.trim().length;
      if (longestLine < length) {
        longestLine = length;
      }
    });
    if (longestLine > 0) {
      if (longestLine >= 11) {
        setHeadlineHasLargeFont(false);
      }
    }
  }, [headline]);

  const isLongLine = (index: number, text: string) => {
    if (headline.length === 3 && index === 0) {
      if (text.length >= 11) {
        return true;
      } else {
        return false;
      }
    }
    if (text.trim().length >= 7) {
      return true;
    }
    return false;
  };

  const animVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }
  return (
    <div className={cx(["inductee-hero-content", className])}>
      <Container className={cx("content")}>
        {/* Headline with multiple lines */}
        <motion.div
          className={cx("headline-container")}
          initial={hasReveal ? 'hidden' : {}}
          animate={hasReveal ? (canAnimateIn ? "visible" : "hidden") : {}}
          transition={{ duration: AnimVals.lg.duration, delay: 0.3, ease: AnimVals.ease }}
          variants={animVariants}
        >
          <div
            className={cx(["headline", {
              'display-xxxxl': headlineHasLargeFont,
              'display-xxxl': !headlineHasLargeFont,
              'has-reverb': showReverb,
              'small-font-size': !headlineHasLargeFont
            }])}
            >
            {headline?.map((line, index) => (
              <div className={cx("line", `line-${headline.length}-${index + 1}`, {'line-long': isLongLine(index, line.line)})} key={index}>
                <span className={cx("line-text")}>{line.line.trim()}</span>
              </div>
            ))}
          </div>
        </motion.div>
        {/* Inductee details: year, category, inducted by */}
        <motion.div
          className={cx("info")}
          initial={hasReveal ? 'hidden' : {}}
          animate={hasReveal ? (canAnimateIn ? "visible" : "hidden") : {}}
          transition={{ duration: AnimVals.lg.duration, delay: 0.4, ease: AnimVals.ease }}
          variants={animVariants}
        >
          <span className={cx("item")}>
            <p className={cx("eyebrow-l", "eyebrow")}>{inducted}</p>
            <p className={cx("title-uc-xs", "value")}>{inducteeClass}</p>
          </span>
          <span className={cx("item")}>
            <p className={cx("eyebrow-l", "eyebrow")}>{categoryLabel}</p>
            <p className={cx("title-uc-xs", "value")}>{categoryParsed?.headline}</p>
          </span>
          { inductedBy && (
            <span className={cx("item")}>
              <p className={cx("eyebrow-l", "eyebrow")}>{inductedByLabel}</p>
              <p className={cx("title-uc-xs", "value")}>{inductedBy}</p>
            </span>
          )}
        </motion.div>
      </Container>
    </div>
  )
}